import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import Navigation from '@/components/Navigation';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Globe, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const [user, setUser] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [region, setRegion] = useState('');
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    if (user) {
      loadRegions(user.currency || 'USD');
    }
  }, [user]);

  const fetchUserData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setCurrency(response.data.currency || 'USD');
      setRegion(response.data.region || '');
    } catch (error) {
      console.error('Error fetching user:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadRegions = async (selectedCurrency) => {
    try {
      let endpoint = '';
      if (selectedCurrency === 'USD') {
        endpoint = '/api/regions/us-states';
      } else if (selectedCurrency === 'EUR') {
        endpoint = '/api/regions/eu-countries';
      } else if (selectedCurrency === 'HKD') {
        endpoint = '/api/regions/hkd-regions';
      }
      
      const response = await axios.get(`${BACKEND_URL}${endpoint}`, {
        withCredentials: true
      });
      setRegions(response.data.regions || []);
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  };

  const handleSaveCurrency = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/user/currency`,
        { currency },
        { withCredentials: true }
      );
      
      if (region) {
        await axios.put(
          `${BACKEND_URL}/api/user/region`,
          { region },
          { withCredentials: true }
        );
      }
      
      toast.success('Settings updated successfully!');
      await fetchUserData();
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
    setRegion('');
    loadRegions(newCurrency);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="settings">
      <Navigation />
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
          <h1 className="font-heading font-black text-4xl md:text-5xl mb-2">Settings</h1>
          <p className="text-muted-foreground">Manage your account and preferences</p>
        </motion.div>

        <div className="max-w-2xl space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="bg-card border-card-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Profile Information
                </CardTitle>
                <CardDescription>Your account details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-muted-foreground text-sm">Name</Label>
                  <p className="text-lg font-medium">{user?.name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Email</Label>
                  <p className="text-lg font-medium">{user?.email || 'N/A'}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Card className="bg-card border-card-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-secondary" />
                  Currency Settings
                </CardTitle>
                <CardDescription>Choose your primary currency for tracking</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="currency">Base Currency</Label>
                  <Select value={currency} onValueChange={handleCurrencyChange}>
                    <SelectTrigger className="bg-background border-card-border w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-card-border">
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="HKD">HKD - Hong Kong Dollar</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-2">
                    This will be used to calculate your personal inflation rate and compare with official rates.
                  </p>
                </div>

                {regions.length > 0 && (
                  <div>
                    <Label htmlFor="region">Region / State</Label>
                    <Select value={region} onValueChange={setRegion}>
                      <SelectTrigger className="bg-background border-card-border w-full">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-card-border">
                        {regions.map((r) => (
                          <SelectItem key={r.code} value={r.code}>
                            {r.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-2">
                      {currency === 'USD' && 'Select your state for more accurate regional inflation data.'}
                      {currency === 'EUR' && 'Select your country within the Eurozone.'}
                      {currency === 'HKD' && 'Select Hong Kong local or US HKD-converted rates.'}
                    </p>
                  </div>
                )}

                <Button 
                  onClick={handleSaveCurrency}
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <Card className="bg-card border-card-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <SettingsIcon className="w-5 h-5 text-accent" />
                  Connected Accounts
                </CardTitle>
                <CardDescription>Manage your bank connections</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Bank connections can be managed from the Expense Tracking page.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
